/**
 * Flythrough authoring + playback panel.
 *
 * Self-contained: the only inputs are a `map` and an opaque `scope` string, so
 * the same component mounts on the ramp, the event planner and the cinematic
 * studio without knowing anything about any of them.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Camera,
  Download,
  Film,
  Gauge,
  Pencil,
  Play,
  Plus,
  Repeat,
  Square,
  Trash2,
  Upload,
} from 'lucide-react'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import { useFlythroughStore, selectActivePlaylist } from './flythroughStore'
import { compileTour, evenPaceDurations, segmentSpeedMph } from './tour'
import { parseFlythroughDoc, serializeFlythroughDoc } from './schema'
import { WaypointRow } from './WaypointRow'
import { runFlythroughSelfChecks } from './selfCheck'

const field =
  'rounded border border-white/10 bg-black/40 px-2 py-1 text-xs text-white/90 outline-none ' +
  'focus:border-sky-400/50'
const sectionTitle = 'text-[10px] font-bold uppercase tracking-widest text-white/30'
const iconBtn =
  'flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-white/50 ' +
  'hover:bg-white/10 hover:text-white disabled:opacity-30'

const SPEEDS = [0.25, 0.5, 1, 2, 4]

let selfChecksRun = false

interface FlythroughPanelProps {
  map: mapboxgl.Map | null
  scope: string
  onClose?: () => void
  className?: string
}

export function FlythroughPanel({ map, scope, onClose, className }: FlythroughPanelProps) {
  const byScope = useFlythroughStore((s) => s.byScope)
  const isPlaying = useFlythroughStore((s) => s.isPlaying)
  const playingScope = useFlythroughStore((s) => s.playingScope)
  const elapsedMs = useFlythroughStore((s) => s.elapsedMs)
  const totalMs = useFlythroughStore((s) => s.totalMs)

  const store = useFlythroughStore
  const scopeState = byScope[scope] ?? { playlists: [], activeId: null }
  const playlist = selectActivePlaylist(useFlythroughStore.getState(), scope)

  const [renaming, setRenaming] = useState(false)
  const [nameDraft, setNameDraft] = useState('')
  const dragFrom = useRef<number | null>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  const playingHere = isPlaying && playingScope === scope

  // Dev-only guard on the spline math (no test runner in this project).
  useEffect(() => {
    if (!import.meta.env.DEV || selfChecksRun) return
    selfChecksRun = true
    const results = runFlythroughSelfChecks()
    const failed = results.filter((r) => !r.pass)
    if (failed.length) console.error('[flythrough] self-checks FAILED', failed)
    else console.info('[flythrough] self-checks passed', results.length)
  }, [])

  // Compiled tour powers the per-shot speed readout and the total-length label.
  const tour = useMemo(() => (playlist ? compileTour(playlist) : null), [playlist])

  const speedByWaypoint = useMemo(() => {
    const map_: Record<number, number> = {}
    if (!tour) return map_
    for (const seg of tour.segments) map_[seg.from] = segmentSpeedMph(seg)
    return map_
  }, [tour])

  const canPlay = !!playlist && playlist.waypoints.length >= 2

  const readCamera = () => {
    if (!map) return null
    const c = map.getCenter()
    return {
      center: [c.lng, c.lat] as [number, number],
      zoom: map.getZoom(),
      pitch: map.getPitch(),
      bearing: map.getBearing(),
    }
  }

  const handleCapture = () => {
    const pose = readCamera()
    if (!pose) return
    // Capturing with no tour selected creates one — `addPlaylist` makes it
    // active, so the capture below lands in it.
    if (!scopeState.activeId) store.getState().addPlaylist(scope)
    store.getState().captureWaypoint(scope, pose)
  }

  const handlePlay = () => {
    if (!playlist || !tour) return
    store.getState().beginPlayback(scope, tour.totalMs)
  }

  const handleEvenPace = () => {
    if (!playlist) return
    const durations = evenPaceDurations(playlist)
    if (!durations) return
    store.getState().setDurations(scope, durations)
    toast.success('Durations rebalanced for a constant-speed glide.')
  }

  const handleExport = () => {
    if (!scopeState.playlists.length) return
    const doc = serializeFlythroughDoc(scopeState.playlists)
    const blob = new Blob([JSON.stringify(doc, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'flythrough.json'
    a.click()
    // Revoke on the next tick — revoking synchronously can cancel the download.
    setTimeout(() => URL.revokeObjectURL(url), 0)
  }

  const handleImportFile = async (file: File) => {
    try {
      const parsed = parseFlythroughDoc(JSON.parse(await file.text()))
      if (!parsed.ok) {
        toast.error(`Could not import: ${parsed.error}`)
        return
      }
      store.getState().importPlaylists(scope, parsed.playlists)
      toast.success(`Imported ${parsed.playlists.length} tour(s).`)
    } catch {
      toast.error('Could not import: the file is not valid JSON.')
    }
  }

  const progress = totalMs > 0 ? Math.min(1, elapsedMs / totalMs) : 0

  return (
    <div
      className={cn(
        'pointer-events-auto flex max-h-[70vh] w-80 flex-col rounded-xl border border-white/10',
        'bg-slate-950/90 text-white backdrop-blur-md',
        className
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
        <Film className="h-4 w-4 text-sky-300" />
        <span className={sectionTitle}>Flythrough</span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          className={iconBtn}
          title="Import tours from JSON"
        >
          <Upload className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={handleExport}
          disabled={!scopeState.playlists.length}
          className={iconBtn}
          title="Export tours as JSON"
        >
          <Download className="h-3.5 w-3.5" />
        </button>
        {onClose && (
          <button type="button" onClick={onClose} className={iconBtn} title="Close">
            <span className="text-base leading-none">×</span>
          </button>
        )}
      </div>

      <input
        ref={fileInput}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0]
          if (file) void handleImportFile(file)
          e.target.value = ''
        }}
      />

      {/* Playlist selector */}
      <div className="flex items-center gap-1.5 border-b border-white/10 px-3 py-2">
        {renaming && playlist ? (
          <input
            autoFocus
            value={nameDraft}
            onChange={(e) => setNameDraft(e.target.value)}
            onBlur={() => {
              const n = nameDraft.trim()
              if (n) store.getState().renamePlaylist(scope, playlist.id, n)
              setRenaming(false)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur()
              if (e.key === 'Escape') setRenaming(false)
            }}
            className={cn(field, 'flex-1')}
          />
        ) : (
          <select
            value={scopeState.activeId ?? ''}
            onChange={(e) => store.getState().setActivePlaylist(scope, e.target.value || null)}
            disabled={playingHere}
            className={cn(field, 'flex-1')}
          >
            {scopeState.playlists.length === 0 && <option value="">No tours yet</option>}
            {scopeState.playlists.map((p) => (
              <option key={p.id} value={p.id} className="bg-slate-900">
                {p.name}
              </option>
            ))}
          </select>
        )}

        <button
          type="button"
          disabled={!playlist || playingHere}
          onClick={() => {
            if (!playlist) return
            setNameDraft(playlist.name)
            setRenaming(true)
          }}
          className={iconBtn}
          title="Rename tour"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          disabled={playingHere}
          onClick={() => store.getState().addPlaylist(scope)}
          className={iconBtn}
          title="New tour"
        >
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          disabled={!playlist || playingHere}
          onClick={() => playlist && store.getState().deletePlaylist(scope, playlist.id)}
          className={iconBtn}
          title="Delete tour"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Waypoints */}
      <div className="min-h-0 flex-1 space-y-1.5 overflow-y-auto px-3 py-2">
        {!playlist || playlist.waypoints.length === 0 ? (
          <p className="px-1 py-6 text-center text-[11px] leading-relaxed text-white/40">
            No shots yet.
            <br />
            Position the camera, then capture a view.
          </p>
        ) : (
          playlist.waypoints.map((w, i) => (
            <WaypointRow
              key={w.id}
              waypoint={w}
              index={i}
              speedMph={speedByWaypoint[i] ?? null}
              isTerminal={!playlist.loop && i === playlist.waypoints.length - 1}
              isCurrent={false}
              disabled={playingHere}
              onRename={(name) => store.getState().updateWaypoint(scope, w.id, { name })}
              onChange={(patch) => store.getState().updateWaypoint(scope, w.id, patch)}
              onPreview={() =>
                map?.easeTo({
                  center: w.center,
                  zoom: w.zoom,
                  pitch: w.pitch,
                  bearing: w.bearing,
                  duration: 800,
                })
              }
              onDelete={() => store.getState().deleteWaypoint(scope, w.id)}
              onDragStart={() => {
                dragFrom.current = i
              }}
              onDrop={() => {
                if (dragFrom.current != null) {
                  store.getState().moveWaypoint(scope, dragFrom.current, i)
                  dragFrom.current = null
                }
              }}
            />
          ))
        )}
      </div>

      {/* Capture */}
      <div className="px-3 pb-2">
        <button
          type="button"
          onClick={handleCapture}
          disabled={!map || playingHere}
          className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed
                     border-white/20 px-2 py-2 text-[11px] font-semibold text-white/70
                     hover:border-sky-400/40 hover:bg-sky-500/10 hover:text-white disabled:opacity-30"
        >
          <Camera className="h-3.5 w-3.5" />
          Capture Current View
        </button>
      </div>

      {/* Playback */}
      <div className="space-y-2 border-t border-white/10 px-3 py-2">
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            onClick={playingHere ? () => store.getState().endPlayback() : handlePlay}
            disabled={!canPlay}
            className={cn(
              'flex h-8 flex-1 items-center justify-center gap-1.5 rounded-md border text-xs font-semibold',
              playingHere
                ? 'border-rose-400/40 bg-rose-500/20 text-rose-100 hover:bg-rose-500/30'
                : 'border-sky-400/40 bg-sky-500/20 text-sky-100 hover:bg-sky-500/30',
              !canPlay && 'opacity-30'
            )}
            title={canPlay ? undefined : 'Capture at least two shots first'}
          >
            {playingHere ? <Square className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            {playingHere ? 'Stop' : 'Play'}
          </button>

          <button
            type="button"
            onClick={handleEvenPace}
            disabled={!canPlay || playingHere}
            className={cn(iconBtn, 'h-8 w-8')}
            title="Even pace — rebalance durations so the whole tour glides at one speed"
          >
            <Gauge className="h-3.5 w-3.5" />
          </button>

          <button
            type="button"
            onClick={() => playlist && store.getState().updatePlaylist(scope, playlist.id, { loop: !playlist.loop })}
            disabled={!playlist || playingHere}
            className={cn(
              iconBtn,
              'h-8 w-8',
              playlist?.loop && 'bg-sky-500/20 text-sky-200 hover:bg-sky-500/30'
            )}
            title="Loop the tour"
          >
            <Repeat className="h-3.5 w-3.5" />
          </button>
        </div>

        {/* Progress */}
        {playingHere && (
          <div className="h-1 w-full overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full bg-sky-400 transition-[width] duration-100 ease-linear"
              style={{ width: `${progress * 100}%` }}
            />
          </div>
        )}

        <div className="flex items-center justify-between gap-2">
          <label className="flex items-center gap-1.5 text-[10px] text-white/50">
            <input
              type="checkbox"
              checked={playlist?.hideUi ?? true}
              disabled={!playlist || playingHere}
              onChange={(e) =>
                playlist && store.getState().updatePlaylist(scope, playlist.id, { hideUi: e.target.checked })
              }
              className="h-3 w-3 accent-sky-400"
            />
            Hide UI
          </label>

          <div className="flex overflow-hidden rounded-md border border-white/10">
            {SPEEDS.map((s) => (
              <button
                key={s}
                type="button"
                disabled={!playlist || playingHere}
                onClick={() => playlist && store.getState().updatePlaylist(scope, playlist.id, { speed: s })}
                className={cn(
                  'px-1.5 py-0.5 text-[10px] font-medium tabular-nums',
                  playlist?.speed === s
                    ? 'bg-sky-500/30 text-sky-200'
                    : 'text-white/45 hover:bg-white/5'
                )}
              >
                {s}×
              </button>
            ))}
          </div>
        </div>

        {tour && (
          <p className="text-[10px] text-white/35">
            {playlist?.waypoints.length} shots · {(tour.totalMs / 1000).toFixed(1)}s
            {playlist?.loop ? ' · looping' : ''}
          </p>
        )}
      </div>
    </div>
  )
}
